import { useContext, useState } from "react";
import {
  FormContextDataProps,
  FormState,
  Props,
  defaultFormState,
} from "../../types/componentTypes";

const useForm = <T extends FormContextDataProps<any>, U extends Props<any>>(
  context: React.Context<T | null>,
  props: U
) => {
  const { activateMutation, deleteMutation } = props;
  const formContext = useContext(context);
  const [actionColumnAnchorEl, setActionColumnAnchorEl] =
    useState<HTMLElement | null>(null);
  const [state, setState] = useState<FormState>(defaultFormState);

  const toggleNewViewHandler = async () => {
    await formContext?.setId(null);
    setState((prevState) => ({
      ...prevState,
      toggleNewForm: !prevState.toggleNewForm,
    }));
  };

  const toggleViewHandler = () => {
    handleClose();
    setState((prevState) => ({
      ...prevState,
      toggleView: !prevState.toggleView,
    }));
  };

  const toggleEditViewHandler = () => {
    setState((prevState) => ({
      ...prevState,
      toggleEditForm: !prevState.toggleEditForm,
      toggleView: false,
    }));
  };

  const toggleDeactivateHandler = () => {
    setState((prevState) => ({
      ...prevState,
      isDeactivateDialogOpen: !prevState.isDeactivateDialogOpen,
      toggleView: false,
    }));
  };

  const toggleActivateHandler = () => {
    setState((prevState) => ({
      ...prevState,
      isActivateDialogOpen: !prevState.isActivateDialogOpen,
    }));
  };

  const handleClose = () => setActionColumnAnchorEl(null);

  const handleDeactivate = () => {
    if (formContext?.id) {
      toggleDeactivateHandler();
      if (deleteMutation)
        deleteMutation(formContext?.id, {
          onSuccess: () => {
            onShowSuccess(`${formContext?.data?.name} deactivated.`);
          },
        });
    }
  };

  const handleActivate = () => {
    if (formContext?.id) {
      toggleActivateHandler();
      if (activateMutation)
        activateMutation(formContext?.id, {
          onSuccess: () => {
            onShowSuccess(`${formContext?.data?.name} activated.`);
          },
        });
    }
  };

  const onShowSuccess = (message: string, refetchGrid: boolean = true) => {
    setState((prevState) => ({
      ...prevState,
      ...defaultFormState,
      showSuccessSnackbar: true,
      successMessage: message,
    }));
    if (refetchGrid) formContext?.refetchGrid();

    formContext?.setId(null);
  };

  const onAddSnackbarClose = () => {
    setState((prevState) => ({
      ...prevState,
      showSuccessSnackbar: false,
    }));
  };

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setActionColumnAnchorEl(event.currentTarget);

  const rowEvents = {
    onClick: async (
      e: React.MouseEvent<HTMLElement, MouseEvent>,
      row: any,
      rowIndex: string
    ) => {
      if (row.id) await formContext?.setId(row.id);
    },
  };

  const handleActiveToggleChange = async () => {
    if (formContext?.setActiveToggleValue)
      await formContext?.setActiveToggleValue();
    formContext?.refetchGrid();
  };

  return {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleNewViewHandler,
    toggleViewHandler,
    toggleEditViewHandler,
    toggleDeactivateHandler,
    toggleActivateHandler,
    handleClose,
    handleDeactivate,
    handleActivate,
    onShowSuccess,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
    handleActiveToggleChange,
  };
};

export default useForm;
